// src/pages/HowItWorks.jsx
import React, { useState, useEffect } from "react"; // Add useState and useEffect
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import './LandingPage.css'; // Import landing page styles for navbar, footer, background
import './HowItWorks.css'; // Import how-it-works specific styles

export default function HowItWorks() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false); // Add mobile menu state
  const [isScrolled, setIsScrolled] = useState(false); // Add scroll state

  // Add scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="container">
      <div className="background-overlay"></div>
      
      {/* Same navbar as landing page with floating effect */}
      <div className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="logo-container">
          <Link to="/">
            <img src="/ob_logo_small_white.png" alt="OffsetBridge logo" className="logo-img" />
          </Link>
          <Link to="/" className="logo-text">OffsetBridge</Link>
        </div>
        
        {/* Desktop Navigation */}
        <div className="nav-buttons desktop-nav">
          <button className="howItWorks-btn" onClick={() => navigate("/how-it-works")}>
            How It Works
          </button>
          <button className="login-btn" onClick={() => navigate("/login")}>
            Log In
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn" 
          onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <button 
          className="mobile-menu-item" 
          onClick={() => {
            navigate("/how-it-works");
            setMobileMenuOpen(false);
          }}
        >
          How It Works
        </button>
        <button 
          className="mobile-menu-item" 
          onClick={() => {
            navigate("/login");
            setMobileMenuOpen(false);
          }}
        >
          Log In
        </button>
      </div>

      {/* How It Works content */}
      <div className="how-it-works-content">
          <h1 className="how-it-works-title">How It Works</h1>
          
          <div className="how-it-works-section">
            <h2 className="how-it-works-goal">Goal: make offset retirement data comprehensive, clean, and actionable</h2>
            <p>
              OffsetBridge was built to answer a simple question: <em>Who buys what in the Voluntary Carbon Market (VCM)?</em> The answer is hidden across fragmented, inconsistently formatted registries. Our goal is to create a unified, structured database that aggregates retirement data from major carbon standards and surfaces the corporate beneficiaries (buyers) behind as many retirements as possible. By standardizing disparate formats and linking them to real companies, we enable more powerful market analysis and improve transparency.
            </p>
          </div>
          
          {/* Process Overview Section */}
          <div className="process-overview-section">
            {/* Desktop view - steps with vertical lines */}
            <div className="process-steps desktop-steps">
              <div className="process-step">
                <h3 className="process-step-title">
                  Aggregate & standardize VCM data
                </h3>
                <div className="vertical-line"></div>
              </div>
              
              <div className="process-step">
                <h3 className="process-step-title">
                  Identify & enrich <span className="highlight-buyers">buyer</span> info
                </h3>
                <div className="vertical-line"></div>
              </div>
              
              <div className="process-step">
                <h3 className="process-step-title">
                  Visualize market intelligence
                </h3>
                <div className="vertical-line"></div>
              </div>
            </div>

            {/* Mobile view - enumerated list */}
            <ol className="process-steps-mobile">
              <li>Aggregate & standardize VCM data</li>
              <li>Identify & enrich <span className="highlight-buyers">buyer</span> info</li>
              <li>Visualize market intelligence</li>
            </ol>
            
            <div className="process-animation-container">
              <video 
                className="process-animation"
                autoPlay 
                loop 
                muted 
                playsInline
              >
                <source src="/graph-scheme-web-15 sec.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          <div className="how-it-works-section">
            <h2 className="how-it-works-heading">Step 1: aggregate & standardize VCM data</h2>
            <p>
              We start by ingesting raw retirement data from leading VCM registries. 
              Currently, OffsetBridge data includes retirements from{" "}
              <a href="https://verra.org" target="_blank" rel="noopener noreferrer" className="registry-link"><strong>Verra</strong></a>,{" "}
              <a href="https://www.goldstandard.org" target="_blank" rel="noopener noreferrer" className="registry-link"><strong>Gold Standard</strong></a>,{" "}
              <a href="https://puro.earth" target="_blank" rel="noopener noreferrer" className="registry-link"><strong>Puro Earth</strong></a>,{" "}
              <a href="https://americancarbonregistry.org" target="_blank" rel="noopener noreferrer" className="registry-link"><strong>American Carbon Registry (ACR)</strong></a>, and{" "}
              <a href="https://isometric.com" target="_blank" rel="noopener noreferrer" className="registry-link"><strong>Isometric</strong></a>. 
              Each registry publishes retirements in different formats, with different naming conventions and data structures. 
              We clean all this mess, removing noise, handling encoding issues, 
              and ensuring consistent formats. We then merge everything into a single dataset of 
              standardized retirements, creating a reliable foundation for deeper analysis.
            </p>
          </div>

          <div className="how-it-works-section">
            <h2 className="how-it-works-heading">Step 2: identify & enrich buyer info</h2>
            <p>
              At the heart of OffsetBridge is our beneficiary matching engine, the "Bridge", 
              which identifies the <strong>true corporate beneficiary</strong> behind retirements. 
              Registry data is often messy and inconsistent, full of spelling variants. 
              We clean and standardize the text, then apply <strong>fuzzy matching algorithms </strong> 
              using multiple similarity metrics and token sorting. This lets us reliably link messy names to real 
              companies, enabling consistent analysis across registries.
              This is what makes OffsetBridge unique.
            </p>

          <div className="static-image-section">
            <img 
              src="/jpm_example_messy_names.svg" 
              alt="Beneficiary matching process" 
              className="process-diagram"
            />
          </div>

          <p>
              When a match is established, we enrich beneficiary data with key contextual information: 
              <strong> HQ country</strong>, <strong>sector</strong>, and <strong>industry</strong>.
          </p>
          </div>

          <div className="how-it-works-section">
            <h2 className="how-it-works-heading">Step 3: visualize market intelligence</h2>
            <p>
              How do we make this data as useful as possible? By answering your questions immediately.
              Click on any beneficiary, intermediary, or project to view up-to-date analytics describing how they behave on the market.
              Retirement histories, portfolio composition, top projects bought, and more. Filter dynamically and find answers in seconds.
              Whether you're a looking for leads, benchmarking peers, or comparing your competitors' offsetting strategies, 
              OffsetBridge turns disjointed registry data into structured, actionable insights.
            </p>
          </div>
          <div className="how-it-works-section">
            <h2 className="how-it-works-heading">Result: clean retirement data that bridges supply and demand</h2>
            <p>
              All retirement transactions whereby we have identified either an end-beneficiary, an intermediary, or both, 
              are shown in the platform. We purposefully discard all transactions that do not provide reliable buyer data, 
              as they are not useful to our users.
            </p>
            <p>
              This is our modest contribution to making the VCM more efficient and more transparent. 
              Our hope is that carbon-reducing & -removing projects thrive and scale with a high-integrity marketplace, 
              and this requires a reliable data infrastructure.
            </p>
            <p>
              If you have any feedback, questions, or thoughts about how we can improve our product, please reach out at{" "}
              <a href="mailto:team@offsetbridge.com" className="how-it-works-email">team@offsetbridge.com</a> — we'd love to chat!
            </p>
          </div>
      </div>

      {/* Same footer as landing page */}
      <div className="footer">
        © 2025 OffsetBridge
        <Link to="/privacy-policy" className="privacy-link">
          Privacy Policy
        </Link>
        <Link to="/terms-and-conditions" className="terms-link">
          Terms & Conditions
        </Link>
      </div>
    </div>
  );
}
